import {
    applyTransaction,
    combineQueries,
    QueryConfigOptions,
    selectPersistStateInit,
} from '@datorama/akita';
import { Subject, Subscription, Observable } from 'rxjs';
import { map, skip } from 'rxjs/operators';
import { defaultUnsubscribeTimoutPeriod } from '../constants/clear-timeout-period';
import { IEntity } from '../interfaces/entity.interface';
import { EntitiesState, EntitiesStore } from './entities.store';
import { QueryEntityWithHighlight } from './highlight.query';

export abstract class EntitiesQuery<
    T extends IEntity,
> extends QueryEntityWithHighlight<EntitiesState<T>> {
    private _instanceCounter = 0;
    private _shuoldFetchEntitiesSubscription: Subscription | null = null;
    private _unsubscribeTimeout: NodeJS.Timeout | null = null;

    private _init$ = new Subject();
    private _hasInitialized = false;

    get hasInitialized() {
        return this._hasInitialized;
    }

    readonly init$ = this._init$.asObservable();
    readonly searchTerm$!: Observable<string>;
    readonly pageNumber$!: Observable<number>;
    readonly pageSize$!: Observable<number>;
    readonly filter$!: Observable<EntitiesState<T>['ui']['filter']>;
    readonly orderBy$!: Observable<EntitiesState<T>['ui']['orderBy']>;
    readonly orderDirection$!: Observable<
        EntitiesState<T>['ui']['orderDirection']
    >;
    readonly total$!: Observable<EntitiesState<T>['total']>;
    readonly currentPageIds$!: Observable<string[]>;
    readonly pagesIds$!: Observable<string[][]>;
    readonly currentPageEntities$!: Observable<T[]>;
    readonly allPageEntities$!: Observable<T[]>;
    readonly shuoldFetchEntities$!: Observable<
        [
            string,
            number,
            number,
            EntitiesState<T>['ui']['filter'],
            EntitiesState<T>['ui']['orderBy'],
            EntitiesState<T>['ui']['orderDirection'],
        ]
    >;
    readonly actionStatus$!: Observable<EntitiesState<T>['actionStatus']>;

    constructor(
        public store: EntitiesStore<T, any, any>,
        options?: Partial<QueryConfigOptions<any>>
    ) {
        super(store, options);

        this.searchTerm$ = this.select(({ ui }) => ui.searchTerm);
        this.pageNumber$ = this.select(({ ui }) => ui.pageNumber);
        this.pageSize$ = this.select(({ ui }) => ui.pageSize);
        this.filter$ = this.select(({ ui }) => ui.filter);
        this.orderBy$ = this.select(({ ui }) => ui.orderBy);
        this.orderDirection$ = this.select(({ ui }) => ui.orderDirection);
        this.total$ = this.select(({ total }) => total);
        this.currentPageIds$ = this.select(
            ({ currentPageIds }) => currentPageIds
        );
        this.pagesIds$ = this.select(({ pagesIds }) => pagesIds);
        this.currentPageEntities$ = combineQueries([
            this.currentPageIds$,
            this.selectAll({ asObject: true }),
        ]).pipe(map(([ids, entities]) => ids.map((id) => entities[id])));
        this.allPageEntities$ = combineQueries([
            this.pagesIds$,
            this.selectAll({ asObject: true }),
        ]).pipe(
            map(([pagesIds, entities]) =>
                Array.from(new Set(([] as string[]).concat(...pagesIds))).map(
                    (id) => entities[id]
                )
            )
        );
        this.shuoldFetchEntities$ = combineQueries([
            this.searchTerm$,
            this.pageSize$,
            this.pageNumber$,
            this.filter$,
            this.orderBy$,
            this.orderDirection$,
        ]);
        this.actionStatus$ = this.select('actionStatus');

        selectPersistStateInit().subscribe(() => {
            this._hasInitialized = true;
            this._init$.next(null); //TODO: CHECK change, added null as argument, required by tS
        });
    }

    /**
     * Start listening to shuoldFetchEntities$ and call fetch on every emit
     * This is used in WithCurrentPageListener and called on component did mount
     */
    startCurrentPageListener = () => {
        if (this._unsubscribeTimeout) {
            clearTimeout(this._unsubscribeTimeout);
            this._unsubscribeTimeout = null;
        }

        this._instanceCounter++;

        setTimeout(async () => {
            if (!this.hasInitialized) {
                this.init$.subscribe(async () => {
                    const { currentPageIds } = this.store.getValue();

                    if (!currentPageIds.length) {
                        await this.store.fetchEntities();
                    }
                });
            } else {
                const { currentPageIds } = this.store.getValue();

                if (!currentPageIds.length) {
                    await this.store.fetchEntities();
                }
            }

            if (!this._shuoldFetchEntitiesSubscription) {
                this._shuoldFetchEntitiesSubscription =
                    this.shuoldFetchEntities$
                        .pipe(skip(1))
                        .subscribe(async () => {
                            if (this.hasInitialized) {
                                await this.store.fetchEntities();
                            }
                        });
            }
        });
    };

    /**
     * Stop listening to shuoldFetchEntities$ and clear current page entities after unsubscribeTimoutPeriod
     * This is used in WithCurrentPageListener and called on component unmount
     */
    stopCurrentPageListener = (
        unsubscribeTimoutPeriod = defaultUnsubscribeTimoutPeriod
    ): void => {
        if (--this._instanceCounter) {
            return;
        }

        if (this._shuoldFetchEntitiesSubscription) {
            this._shuoldFetchEntitiesSubscription.unsubscribe();
            this._shuoldFetchEntitiesSubscription = null;
        }

        if (!this._unsubscribeTimeout) {
            this._unsubscribeTimeout = setTimeout(() => {
                applyTransaction(() => {
                    this.store.setCurrentPageEntities([]);
                    this.store.patchUIState({ searchTerm: '', pageNumber: 1 });
                });
            }, unsubscribeTimoutPeriod);
        }
    };
}
